import { LitElement } from "lit";

export class CarouselControls {
  host: LitElement;

  offset = 0;

  itemCount: number;

  activeIndex: number;

  #carousel?: HTMLElement | null;

  #id: string;

  #maxPerView: number = 4;

  get scrollStep() {
    return 100 / this.cardsPerView;
  }

  get cardsPerView() {
    const w = document.body.offsetWidth;

    // eslint-disable-next-line no-nested-ternary
    const max = w < 767 ? 1 : w < 1024 ? 2 : w < 1400 ? 3 : 4;
    return Math.min(max, this.#maxPerView);
  }

  get first() {
    return Math.trunc(this.offset) === 0;
  }

  get last() {
    return (
      this.offset === (this.itemCount - this.cardsPerView) * -this.scrollStep
    );
  }

  get allInOne() {
    return this.cardsPerView >= this.itemCount;
  }

  constructor(
    host: LitElement,
    itemCount: number,
    maxPerView: number = 4,
    id: string = "carousel",
    activeIndex?: number
  ) {
    this.host = host;
    this.itemCount = itemCount;
    this.#maxPerView = maxPerView;
    this.#id = id;
    this.activeIndex = activeIndex ?? 0;
  }

  slide(action: "next" | "prev") {
    this.#carousel = this.host.renderRoot.querySelector(`#${this.#id}`);
    if (!this.#carousel) return;

    const direction = action === "next" ? -1 : 1;

    if ((direction === 1 && this.first) || (direction === -1 && this.last)) {
      return;
    }

    this.offset += this.scrollStep * direction;
    this.activeIndex -= direction; // inverted as moving to next is -1, prev is 1

    this.#transform();
  }

  resize() {
    this.#carousel = this.host.renderRoot.querySelector(`#${this.#id}`);
    if (!this.#carousel) return;

    this.offset =
      this.activeIndex > 0 && this.itemCount > this.cardsPerView
        ? -this.activeIndex * this.scrollStep
        : 0;

    this.#transform();
  }

  initialize() {
    this.resize();
  }

  #transform() {
    if (!this.#carousel) return;

    // Read the CSS variable value from the host element
    // because this value changes on resize, we don't need to pass it in as a property.
    const styles = getComputedStyle(this.host);
    const offsetValue =
      styles?.getPropertyValue("--carousel-gap")?.trim() || "0";
    let offset = parseInt(offsetValue, 10);
    offset = this.cardsPerView > 1 ? offset / 2 : offset;

    this.#carousel.style.transform = `translateX(calc(${this.offset}% + ${
      this.activeIndex * -offset
    }px))`;

    this.#setButtonStates();
  }

  #setButtonStates() {
    const header = this.host.renderRoot.querySelector(
      "umb-market-card-deck-header"
    )?.shadowRoot;

    if (!header) return;

    const prevButton = header.querySelector('[data-action="prev"]');
    const nextButton = header.querySelector('[data-action="next"]');

    if (prevButton) {
      if (this.first) {
        prevButton.setAttribute("disabled", "disabled");
      } else {
        prevButton.removeAttribute("disabled");
      }
    }

    if (!nextButton) return;

    if (this.last) {
      nextButton.setAttribute("disabled", "disabled");
    } else {
      nextButton.removeAttribute("disabled");
    }
  }
}
